/**
 * Pure derivation of the active top-level view.
 *
 * Priority order is load-bearing for the multiplex contract:
 *   list > lists-overview > team > automations > skills > connected-tools > library > invites > multiplex > inbox
 *
 * `invites` (like `library`) is a Home-scope page — NOT gated on
 * `scope !== "home"` — so it works from the Home nav. It sits just
 * below library and above multiplex so a selected channel doesn't
 * shadow it.
 *
 * The `!selectedListId` / `!showLists` chains preserve that order without
 * a switch statement — flipping any of them to a higher-priority active
 * flag must demote the lower ones in lockstep.
 */
export interface ActiveViewInputs {
  scope: string;
  selectedChannel: string | null;
  selectedWorkspace: string | null;
  selectedListId: string | null;
  showLists: boolean;
  showCoworkers: boolean;
  showAutomations: boolean;
  showSkills: boolean;
  showConnectedTools: boolean;
  showLibrary: boolean;
  showInvites: boolean;
}

export interface ActiveViewFlags {
  isListActive: boolean;
  isListsOverviewActive: boolean;
  isTeamActive: boolean;
  isAutomationsActive: boolean;
  isSkillsActive: boolean;
  isConnectedToolsActive: boolean;
  isLibraryActive: boolean;
  isInvitesActive: boolean;
  isMultiplexActive: boolean;
  isInboxActive: boolean;
}

export function deriveActiveView(input: ActiveViewInputs): ActiveViewFlags {
  const {
    scope,
    selectedChannel,
    selectedWorkspace,
    selectedListId,
    showLists,
    showCoworkers,
    showAutomations,
    showSkills,
    showConnectedTools,
    showLibrary,
    showInvites,
  } = input;
  const isListActive = Boolean(selectedListId && scope !== "home");
  const isListsOverviewActive = Boolean(
    showLists && scope !== "home" && !selectedListId,
  );
  const isTeamActive = Boolean(
    showCoworkers &&
    scope !== "home" &&
    !selectedListId &&
    !showLists &&
    selectedWorkspace,
  );
  const isAutomationsActive = Boolean(
    showAutomations &&
    scope !== "home" &&
    !selectedListId &&
    !showLists &&
    !showCoworkers &&
    selectedWorkspace,
  );
  const isSkillsActive = Boolean(
    showSkills &&
    scope !== "home" &&
    !selectedListId &&
    !showLists &&
    !showCoworkers &&
    !showAutomations &&
    selectedWorkspace,
  );
  const isConnectedToolsActive = Boolean(
    showConnectedTools &&
    scope !== "home" &&
    !selectedListId &&
    !showLists &&
    !showCoworkers &&
    !showAutomations &&
    !showSkills &&
    selectedWorkspace,
  );
  const isLibraryActive = Boolean(
    showLibrary &&
    !selectedListId &&
    !showLists &&
    !showCoworkers &&
    !showAutomations &&
    !showSkills &&
    !showConnectedTools,
  );
  // Home-scope page — not gated on `scope !== "home"`, mirroring library.
  const isInvitesActive = Boolean(
    showInvites &&
    !selectedListId &&
    !showLists &&
    !showCoworkers &&
    !showAutomations &&
    !showSkills &&
    !showConnectedTools &&
    !showLibrary,
  );
  const isMultiplexActive = Boolean(
    selectedChannel &&
    !isListActive &&
    !isListsOverviewActive &&
    !isTeamActive &&
    !isAutomationsActive &&
    !isSkillsActive &&
    !isConnectedToolsActive &&
    !isLibraryActive &&
    !isInvitesActive,
  );
  const isInboxActive =
    !isMultiplexActive &&
    !isListActive &&
    !isListsOverviewActive &&
    !isTeamActive &&
    !isAutomationsActive &&
    !isSkillsActive &&
    !isConnectedToolsActive &&
    !isLibraryActive &&
    !isInvitesActive;
  return {
    isListActive,
    isListsOverviewActive,
    isTeamActive,
    isAutomationsActive,
    isSkillsActive,
    isConnectedToolsActive,
    isLibraryActive,
    isInvitesActive,
    isMultiplexActive,
    isInboxActive,
  };
}

export function useActiveView(input: ActiveViewInputs): ActiveViewFlags {
  return deriveActiveView(input);
}
